import { useCallback } from "react";
import { useQueryClient } from "react-query";
import { referenceDataQueryKeys } from "./queryKeys";
import { valuesEqual } from "../lib/flightTimingUtils";
import { buildRouteReferenceData, getRouteById, mergeReferenceData, normalizeAirportCode } from "../lib/routeUtils";
import { scheduleDraftReferenceDataService } from "../services/scheduleDraftReferenceDataService";

const REFERENCE_ROUTE_STALE_TIME = 5 * 60 * 1000;
const ROUTE_RELATED_FLIGHT_FIELDS = ["flight_route_id", "departure_airport", "arrival_airport"];
const AIRPORT_FIELDS = new Set(["departure_airport", "arrival_airport"]);

function hasOwn(object, key) {
    return Object.prototype.hasOwnProperty.call(object || {}, key);
}

function getEditValue(values = {}, currentFlight = {}, field) {
    return hasOwn(values, field) ? values[field] : currentFlight?.[field];
}

function fieldValueChanged(field, before, after) {
    if (AIRPORT_FIELDS.has(field)) {
        return normalizeAirportCode(before) !== normalizeAirportCode(after);
    }

    return !valuesEqual(field, before, after);
}

function hasChangedField(values = {}, currentFlight = {}, field) {
    return hasOwn(values, field) && fieldValueChanged(field, currentFlight?.[field], values[field]);
}

function hasRouteRelatedFieldChange(values = {}, currentFlight = {}) {
    return ROUTE_RELATED_FLIGHT_FIELDS.some((field) => hasChangedField(values, currentFlight, field));
}

function hasAirportFieldChange(values = {}, currentFlight = {}) {
    return Array.from(AIRPORT_FIELDS).some((field) => hasChangedField(values, currentFlight, field));
}

function hasRouteReference(referenceData = {}, routeId) {
    if (routeId === undefined || routeId === null || routeId === "") {
        return true;
    }

    return Boolean(getRouteById(referenceData, routeId));
}

function withRoutesByLeg(referenceData = {}, departureAirport, arrivalAirport) {
    if (!departureAirport || !arrivalAirport) {
        return referenceData;
    }

    return {
        ...referenceData,
        routesByLeg: {
            ...(referenceData.routesByLeg || {}),
            [`${departureAirport}:${arrivalAirport}`]: referenceData.routes || [],
        },
    };
}

function createRouteReferenceDataLoadError(error) {
    const loadError = new Error(error?.response?.data?.detail || error?.message || "航路数据加载失败");
    loadError.code = "ROUTE_REFERENCE_DATA_LOAD_FAILED";
    return loadError;
}

export default function useFlightEditRouteReferenceData() {
    const queryClient = useQueryClient();

    return useCallback(
        async ({ values, currentFlight, referenceData } = {}) => {
            const baseReferenceData = referenceData || {};
            if (!hasRouteRelatedFieldChange(values, currentFlight)) {
                return baseReferenceData;
            }

            const routeId = getEditValue(values, currentFlight, "flight_route_id");
            if (!hasAirportFieldChange(values, currentFlight) && hasRouteReference(baseReferenceData, routeId)) {
                return baseReferenceData;
            }

            const departureAirport = normalizeAirportCode(getEditValue(values, currentFlight, "departure_airport"));
            const arrivalAirport = normalizeAirportCode(getEditValue(values, currentFlight, "arrival_airport"));
            if (!departureAirport || !arrivalAirport) {
                return baseReferenceData;
            }

            try {
                const routeOptions = await queryClient.fetchQuery(
                    referenceDataQueryKeys.flightRoutes(departureAirport, arrivalAirport),
                    () => scheduleDraftReferenceDataService.getRoutesByLeg(departureAirport, arrivalAirport),
                    { staleTime: REFERENCE_ROUTE_STALE_TIME },
                );
                return mergeReferenceData(
                    baseReferenceData,
                    withRoutesByLeg(
                        buildRouteReferenceData(routeOptions),
                        departureAirport,
                        arrivalAirport,
                    ),
                );
            } catch (error) {
                console.warn("Failed to load flight route reference data:", error);
                throw createRouteReferenceDataLoadError(error);
            }
        },
        [queryClient],
    );
}
